import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { MenuItem } from "primeng/api";
import { AsyncPipe } from "@angular/common";
import { AvatarModule } from "primeng/avatar";
import { ButtonModule } from "primeng/button";
import { HostUtil } from "../../utils/host-util";
import { LocalstorageUtil } from "@zaeper/core-lib";
import {
  userAuthenticationRestClientInjectionToken
} from "../../rest-clients/user/authentication/user-authentication-rest-client-injection-token";
import {
  IUserAuthenticationRestClient
} from "../../rest-clients/user/authentication/i-user-authentication-rest.client";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { BehaviorSubject, Observable, map } from "rxjs";
import { ICurrentUser } from "../../models/i-current-user";
import { blockingLoaderServiceInjectionToken, DropdownMenuComponent, IBlockingLoaderService } from "@zaeper/angular-dashboard-view-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { RoleTextPipe } from "../../pipes/role-text.pipe";
import { TCompanyRole } from "../../models/company-role";
import { ICompany } from "../../models/i-company";
import {
  switchCompanyModalServiceInjectionToken
} from "../../modals/switch-company-modal/switch-company-modal-service-injection-token";
import { ISwitchCompanyModalService } from "../../modals/switch-company-modal/i-switch-company-modal.service";

@Component({
  selector: "app-user-menu",
  imports: [
    AsyncPipe, AvatarModule, ButtonModule, DropdownMenuComponent, ZTemplateDirective, ZDictionaryPipe, RoleTextPipe
  ],
  templateUrl: "./user-menu.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMenuComponent {
  public readonly currentUser$: Observable<ICurrentUser | null>;
  public readonly userMenuItems$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  public readonly initials$: Observable<string>;
  public readonly avatarColor$: Observable<string>;
  /** The user's role in the active company, shown on top of the menu with what it may do. */
  public readonly activeCompany$: Observable<ICompany | null>;
  public readonly activeRole$: Observable<TCompanyRole | null>;

  constructor(
    @Inject(userAuthenticationRestClientInjectionToken) private readonly _userAuthenticationRestClient: IUserAuthenticationRestClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(switchCompanyModalServiceInjectionToken) private readonly _switchCompanyModalService: ISwitchCompanyModalService,
    @Inject(blockingLoaderServiceInjectionToken) private readonly _blockingLoaderService: IBlockingLoaderService
  ) {
    this.currentUser$ = this._sessionStateService.currentUser$;
    this.initials$ = this.currentUser$.pipe(map((user) => this._getInitials(user)));
    this.avatarColor$ = this.currentUser$.pipe(map((user) => user?.color ?? '#9c27b0'));
    this.activeCompany$ = this._sessionStateService.activeCompany$;
    this.activeRole$ = this._sessionStateService.activeRole$;
    this.currentUser$.subscribe((user) => this._buildMenuItems(user));
  }

  private _getInitials(user: ICurrentUser | null): string {
    if (!user) return '?';
    return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
  }

  private _buildMenuItems(_user: ICurrentUser | null): void {
    const items: MenuItem[] = [
      {
        label: "Organisation wechseln",
        icon: "pi pi-building",
        command: () => this._switchCompanyModalService.open()
      },
      {
        label: "Organisationsverwaltung",
        icon: "pi pi-building-columns",
        routerLink: "/de/company"
      },
      {
        label: "Benutzereinstellungen",
        icon: "pi pi-cog",
        routerLink: "/de/user-settings"
      }, {
        label: "Rechnungsübersicht",
        icon: "pi pi-credit-card",
        routerLink: "/de/payment-overview"
      }
    ];

    items.push({
      label: "Abmelden",
      icon: "pi pi-sign-out",
      styleClass: "font-semibold",
      command: () => {
        this._logout();
      }
    });

    this.userMenuItems$.next(items);
  }

  private _logout() {
    this._blockingLoaderService.show();
    this._userAuthenticationRestClient.logout()
      .then(() => {
        const authenticationSiteUrl: string = HostUtil.getAuthenticationSiteUrl();
        LocalstorageUtil.removeCSRFToken();
        location.href = authenticationSiteUrl;
      })
      .catch(() => {
        this._blockingLoaderService.hide();
      });
  }
}
